import { Box, createStyles, Group, Stack, UnstyledButton } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { trackEvent } from '@repo/common/analytics';
import { ContextMenu, Icon, Text } from '@repo/foundations';
import { typography } from '@repo/theme/primitives';
import { includes } from 'lodash-es';
import { useCallback } from 'react';
import { notificationSettingsQueryKeyFactory } from '../../../api/hooks/notification/constants';
import type { INotificationSettings } from '../../../api/types/models/notification';
import { SecodaEntityIcon } from '../../../components/SecodaEntity/SecodaEntityIcon';
import { ANALYTICS_EVENTS, NOTIFICATION_EVENT_TYPES } from '../constants';
import { inboxFeatureStore } from '../store';
import type { EventType, IInboxListItemProps } from '../types';
import { isTeamAccessRequestEvent } from '../utils/teamAccessRequestUtils';
import { NotificationStatus } from './NotificationStatus';
import { NotificationText } from './NotificationText';
import { NotificationTime } from './NotificationTime';

const useStyles = createStyles(
	(theme, { selected, read }: { selected: boolean; read: boolean }) => {
		let opacity = read ? 0.65 : 1;
		if (selected) {
			opacity = 1;
		}

		return {
			container: {
				backgroundColor: selected
					? theme.other.getColor('surface/primary/active')
					: 'transparent',
				borderRadius: theme.radius.sm,
				padding: `${theme.spacing.xs} ${theme.spacing.xs} ${theme.spacing.xs} ${theme.spacing.sm}`,
				alignItems: 'center',
				'&:hover': {
					backgroundColor: selected
						? theme.other.getColor('surface/primary/active')
						: theme.other.getColor('surface/primary/hover'),
				},
				opacity,
			},
			indicator: {
				width: 8,
				height: 8,
				borderRadius: 8,
				display: !read ? 'inline-block' : 'none',
				backgroundColor: !read
					? theme.other.getColor('icon/emphasis/default')
					: 'transparent',
				flexShrink: 0,
			},
			titleContainer: {
				width: '100%',
			},
			title: {
				fontSize: theme.fontSizes.sm,
				fontWeight: typography.weight.regular,
				lineHeight: '20px',
				display: '-webkit-box',
				WebkitLineClamp: 1,
				WebkitBoxOrient: 'vertical',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				maxWidth: '220px',
			},
			subtitle: {
				fontSize: theme.fontSizes.xs,
				fontWeight: typography.weight.regular,
				lineHeight: '16px',
				color: theme.other.getColor('text/secondary/default'),
				maxWidth: '220px',
			},
			button: {
				'&:focus, &:focus-visible, &:active': {
					outline: 'none',
					border: 'none',
					boxShadow: 'none',
				},
			},
		};
	}
);

export function InboxListItem({
	item,
	selected,
	onSelect,
	onKeyDown,
	handleDelete,
	handleMarkAsRead,
	handleRestore,
	queryClient,
	notificationSettingsData,
	updateNotificationSettings,
	user,
	workspace,
}: IInboxListItemProps) {
	const { classes } = useStyles({ selected, read: item.read });
	const notificationSettings = notificationSettingsData
		?.results?.[0] as unknown as INotificationSettings;

	const matchedEventType: EventType | undefined = NOTIFICATION_EVENT_TYPES.find(
		(et) => includes(et.values, item.event)
	);
	const isTeamAccessRequest = isTeamAccessRequestEvent(item.event);
	let iconName: string | undefined;
	if (matchedEventType?.value === 'notify_automation_review') {
		({ iconName } = matchedEventType);
	} else if (isTeamAccessRequest) {
		iconName = 'users';
	}
	const notificationAttribute = matchedEventType?.value as
		| keyof INotificationSettings
		| undefined;

	const showUndoNotification = useCallback(
		(undoFn: () => void, message: string) => {
			const undoAction = {
				type: 'turn_off_notification' as const,
				data: { undoFn },
				timestamp: Date.now(),
			};
			inboxFeatureStore.setPendingUndoAction(undoAction);

			showNotification({
				message: `${message} (Press Ctrl+Z to undo)`,
				color: 'blue',
				autoClose: 8000,
				onClose: () => inboxFeatureStore.clearUndoAction(),
			});

			setTimeout(() => {
				inboxFeatureStore.clearUndoAction();
			}, 8000);
		},
		[]
	);

	const showDeleteUndoNotification = useCallback(
		(undoFn: () => void, message: string) => {
			const undoAction = {
				type: 'delete' as const,
				data: { undoFn },
				timestamp: Date.now(),
			};
			inboxFeatureStore.setPendingUndoAction(undoAction);

			showNotification({
				message: `${message} (Press Ctrl+Z to undo)`,
				color: 'blue',
				autoClose: 8000,
				onClose: () => inboxFeatureStore.clearUndoAction(),
			});

			setTimeout(() => {
				inboxFeatureStore.clearUndoAction();
			}, 8000);
		},
		[]
	);

	const handleTurnOffClick = async () => {
		if (!notificationAttribute || !notificationSettingsData?.results?.[0]?.id)
			return;

		const current = notificationSettings?.[notificationAttribute];
		if (current?.app === false) return; // already off

		// Store original settings for undo
		const originalValue = { ...current };
		const updatedValue = { ...current, app: false } as Record<string, boolean>;

		trackEvent(
			ANALYTICS_EVENTS.NOTIFICATION_TURN_OFF,
			{
				notificationId: item.id,
				notificationEvent: item.event,
				notificationAttribute: notificationAttribute,
				resourceType: item.resource_type || 'unknown',
				fromContext: 'context_menu',
				isSelected: selected.toString(),
				wasRead: item.read.toString(),
			},
			user,
			workspace
		);

		await updateNotificationSettings({
			data: {
				id: notificationSettingsData.results[0].id,
				[notificationAttribute]: updatedValue,
			},
		});

		queryClient.invalidateQueries({
			queryKey: notificationSettingsQueryKeyFactory.allLists(),
		});

		const undoFn = async () => {
			try {
				await updateNotificationSettings({
					data: {
						id: notificationSettingsData.results[0].id,
						[notificationAttribute]: originalValue,
					},
				});

				queryClient.invalidateQueries({
					queryKey: notificationSettingsQueryKeyFactory.allLists(),
				});

				showNotification({
					message: 'Notification type turned back on',
					color: 'green',
				});

				trackEvent(
					ANALYTICS_EVENTS.NOTIFICATION_TURN_OFF_UNDO,
					{
						notificationId: item.id,
						notificationEvent: item.event,
						notificationAttribute: notificationAttribute,
						resourceType: item.resource_type || 'unknown',
					},
					user,
					workspace
				);
			} catch {
				showNotification({
					title: 'Error',
					message: 'Failed to undo turning off notification type',
					color: 'red',
				});
			}
		};

		showUndoNotification(undoFn, 'Turned off this notification type');
	};

	const handleOnClick = () => {
		onSelect(item.id, item.read);
	};

	const handleOnContextMenu = () => {
		trackEvent(
			ANALYTICS_EVENTS.NOTIFICATION_CONTEXT_MENU,
			{
				notificationId: item.id,
				notificationEvent: item.event,
				resourceType: item.resource_type || 'unknown',
				wasRead: item.read.toString(),
				isSelected: selected.toString(),
				notificationAttribute: notificationAttribute || 'unknown',
			},
			user,
			workspace
		);

		// Automatically select the item when right-clicked
		if (!selected) {
			onSelect(item.id, item.read);
		}
	};

	const handleContextMenuOpen = () => {
		trackEvent(
			ANALYTICS_EVENTS.NOTIFICATION_CONTEXT_MENU_OPEN,
			{
				notificationId: item.id,
				notificationEvent: item.event,
				resourceType: item.resource_type || 'unknown',
				wasRead: item.read.toString(),
				isSelected: selected.toString(),
			},
			user,
			workspace
		);
	};

	const handleContextMenuClose = () => {
		trackEvent(
			ANALYTICS_EVENTS.NOTIFICATION_CONTEXT_MENU_CLOSE,
			{
				notificationId: item.id,
				notificationEvent: item.event,
				resourceType: item.resource_type || 'unknown',
				wasRead: item.read.toString(),
				isSelected: selected.toString(),
			},
			user,
			workspace
		);
	};

	const handleMarkAsReadClick = () => {
		trackEvent(
			ANALYTICS_EVENTS.NOTIFICATION_MARK_READ,
			{
				notificationId: item.id,
				notificationEvent: item.event,
				resourceType: item.resource_type || 'unknown',
				action: item.read ? 'mark_unread' : 'mark_read',
				fromContext: 'context_menu',
				isSelected: selected.toString(),
				notificationAttribute: notificationAttribute || 'unknown',
			},
			user,
			workspace
		);

		// Only add to recently read when marking an unread notification as read
		// This keeps it visible temporarily when showRead is off
		// Don't touch recentlyRead when marking as unread - it will be visible as unread anyway
		if (!item.read && !inboxFeatureStore.showRead) {
			inboxFeatureStore.addToRecentlyRead(item.id);
		}

		handleMarkAsRead({ id: item.id });
	};

	const handleDeleteClick = () => {
		trackEvent(
			ANALYTICS_EVENTS.NOTIFICATION_DELETE,
			{
				notificationId: item.id,
				notificationEvent: item.event,
				resourceType: item.resource_type || 'unknown',
				fromContext: 'context_menu',
				isSelected: selected.toString(),
				wasRead: item.read.toString(),
				notificationAttribute: notificationAttribute || 'unknown',
			},
			user,
			workspace
		);

		handleDelete({ id: item.id });

		const undoFn = async () => {
			try {
				await handleRestore({ id: item.id });

				trackEvent(
					ANALYTICS_EVENTS.NOTIFICATION_DELETE_UNDO,
					{
						notificationId: item.id,
						notificationEvent: item.event,
						notificationAttribute: notificationAttribute || 'unknown',
						resourceType: item.resource_type || 'unknown',
					},
					user,
					workspace
				);
			} catch {
				showNotification({
					title: 'Error',
					message: 'Failed to restore notification',
					color: 'red',
				});
			}
		};

		showDeleteUndoNotification(undoFn, 'Deleted notification');
	};

	return (
		<Stack spacing={0} data-testid="inbox-list-item" mb={'3xs'}>
			<ContextMenu
				onOpen={handleContextMenuOpen}
				onClose={handleContextMenuClose}
			>
				<ContextMenu.Target>
					<UnstyledButton
						style={{ outline: 'none', outlineOffset: 0 }}
						onClick={handleOnClick}
						onContextMenu={handleOnContextMenu}
						className={classes.button}
						onKeyDown={onKeyDown}
					>
						<Group
							className={classes.container}
							spacing="sm"
							align="start"
							noWrap
						>
							<Group spacing="xs">
								<SecodaEntityIcon
									size={28}
									showOverlay={true}
									isIntegration={item.computedMetadata?.isIntegration}
									isMonitorIncident={item.computedMetadata?.isMonitorIncident}
									isAiAgent={item.computedMetadata?.isAiAgent}
									integration={item.computedMetadata?.integration}
									incident={item.computedMetadata?.incident}
									user={item.sender}
									entityType={
										isTeamAccessRequest ? undefined : item.resource_type
									}
									notificationEvent={
										isTeamAccessRequest ? undefined : item.event
									}
									icon={iconName}
									emoji={item.metadata?.emoji as string}
									isAutomation={
										matchedEventType?.value === 'notify_automation_review'
									}
								/>
							</Group>
							<Stack className={classes.titleContainer} spacing={1}>
								<Group spacing="xs" noWrap align="baseline">
									<Box className={classes.indicator} />
									<NotificationText
										notification={item}
										className={classes.title}
										showResourceTitle={true}
									/>
								</Group>
								<NotificationText
									notification={item}
									className={classes.subtitle}
									useTextWithLineClamp={true}
									lineClamp={1}
								/>
							</Stack>
							<Stack spacing={2} align="end">
								<NotificationStatus notification={item} />
								<NotificationTime notification={item} />
							</Stack>
						</Group>
					</UnstyledButton>
				</ContextMenu.Target>
				<ContextMenu.Dropdown>
					<ContextMenu.Item
						icon={
							item.read ? <Icon name="notification" /> : <Icon name="check" />
						}
						onClick={handleMarkAsReadClick}
					>
						<Text size="sm">
							{item.read ? 'Mark as unread' : 'Mark as read'}
						</Text>
					</ContextMenu.Item>
					<ContextMenu.Item
						icon={<Icon name="trash" />}
						onClick={handleDeleteClick}
					>
						<Text size="sm">Delete notification</Text>
					</ContextMenu.Item>
					<ContextMenu.Item
						icon={<Icon name="bellOff" />}
						onClick={handleTurnOffClick}
					>
						<Text size="sm">Turn off this notification type</Text>
					</ContextMenu.Item>
				</ContextMenu.Dropdown>
			</ContextMenu>
		</Stack>
	);
}
